import React, { useEffect, lazy, Suspense } from 'react';
import { SEOHead } from '../../components/SEOHead';
import { useLocaleContext } from '../../context/LocaleContext';
import type { Locale } from '../../types/locale';
// LCP OPTIMIZATION: Keep ApiHero as direct import (above-fold)
import { ApiHero } from '../../components/api';

// LCP OPTIMIZATION: Lazy load below-fold components to reduce initial bundle size
const ApiFeatures = lazy(() => import('../../components/api/ApiFeatures').then(m => ({ default: m.ApiFeatures })));
const ApiResponseExample = lazy(() => import('../../components/api/ApiResponseExample').then(m => ({ default: m.ApiResponseExample })));
const ApiErrorResponses = lazy(() => import('../../components/api/ApiErrorResponses').then(m => ({ default: m.ApiErrorResponses })));
const ApiHowItWorks = lazy(() => import('../../components/api/ApiHowItWorks').then(m => ({ default: m.ApiHowItWorks })));
const ApiWatermarkTypes = lazy(() => import('../../components/api/ApiWatermarkTypes').then(m => ({ default: m.ApiWatermarkTypes })));
const ApiUseCases = lazy(() => import('../../components/api/ApiUseCases').then(m => ({ default: m.ApiUseCases })));
const ApiTrustSignals = lazy(() => import('../../components/api/ApiTrustSignals').then(m => ({ default: m.ApiTrustSignals })));
const ApiPricing = lazy(() => import('../../components/api/ApiPricing').then(m => ({ default: m.ApiPricing })));
const ApiFaq = lazy(() => import('../../components/api/ApiFaq').then(m => ({ default: m.ApiFaq })));
const ApiCta = lazy(() => import('../../components/api/ApiCta').then(m => ({ default: m.ApiCta })));

const translations: Record<Locale, {
  seo: {
    title: string;
    description: string;
  };
  hero: {
    title: string;
    subtitle: string;
    ctaText: string;
    secondaryCtaText: string;
  };
  schema: {
    description: string;
    offerDescription: string;
    features: string[];
  };
}> = {
  en: {
    seo: {
      title: 'Watermark Remover API - Remove AI Watermarks Programmatically',
      description: 'REST API to remove invisible AI watermarks from text. Detect and remove all known Unicode watermark characters used by ChatGPT, Claude, and Gemini. Simple JSON API with sub-100ms response times.',
    },
    hero: {
      title: 'Watermark Remover API',
      subtitle: 'Remove invisible AI watermarks from text programmatically. One endpoint. All known watermark types detected. Built for developers who need clean AI text at scale.',
      ctaText: 'Get API Access',
      secondaryCtaText: 'View Documentation',
    },
    schema: {
      description: 'REST API to remove invisible AI watermarks from text. Detect and remove all known Unicode watermark characters used by ChatGPT and other AI tools.',
      offerDescription: '1,000 API calls included with Premium subscription',
      features: [
        'Detects all known invisible Unicode watermark characters',
        'Processes up to 100,000 characters per request',
        '1,000 API calls per month included',
        'Simple REST API with JSON responses',
        'Works with ChatGPT, Claude, Gemini text',
      ],
    },
  },
  de: {
    seo: {
      title: 'Wasserzeichen-Entferner API - KI-Wasserzeichen programmatisch entfernen',
      description: 'REST API zum Entfernen unsichtbarer KI-Wasserzeichen aus Text. Erkennen und entfernen Sie alle bekannten Unicode-Wasserzeichen von ChatGPT, Claude und Gemini. Einfache JSON API mit Antwortzeiten unter 100ms.',
    },
    hero: {
      title: 'Wasserzeichen-Entferner API',
      subtitle: 'Entfernen Sie unsichtbare KI-Wasserzeichen programmatisch aus Text. Ein Endpunkt. Alle bekannten Wasserzeichentypen erkannt. Für Entwickler gebaut, die sauberen KI-Text in großem Umfang benötigen.',
      ctaText: 'API-Zugang erhalten',
      secondaryCtaText: 'Dokumentation ansehen',
    },
    schema: {
      description: 'REST API zum Entfernen unsichtbarer KI-Wasserzeichen aus Text. Erkennt und entfernt alle bekannten Unicode-Wasserzeichenzeichen.',
      offerDescription: '1.000 API-Aufrufe im Premium-Abonnement enthalten',
      features: [
        'Erkennt alle bekannten unsichtbaren Unicode-Wasserzeichenzeichen',
        'Verarbeitet bis zu 100.000 Zeichen pro Anfrage',
        '1.000 API-Aufrufe pro Monat enthalten',
        'Einfache REST API mit JSON-Antworten',
        'Funktioniert mit ChatGPT, Claude, Gemini Text',
      ],
    },
  },
  'pt-BR': {
    seo: {
      title: 'API Removedor de Marcas d\'Água - Remova Marcas d\'Água de IA Programaticamente',
      description: 'API REST para remover marcas d\'água invisíveis de IA do texto. Detecte e remova todos os caracteres Unicode de marca d\'água conhecidos usados por ChatGPT, Claude e Gemini. API JSON simples com tempos de resposta abaixo de 100ms.',
    },
    hero: {
      title: 'API Removedor de Marcas d\'Água',
      subtitle: 'Remova marcas d\'água invisíveis de IA do texto programaticamente. Um endpoint. Todos os tipos de marca d\'água conhecidos detectados. Construída para desenvolvedores que precisam de texto de IA limpo em escala.',
      ctaText: 'Obter Acesso à API',
      secondaryCtaText: 'Ver Documentação',
    },
    schema: {
      description: 'API REST para remover marcas d\'água invisíveis de IA do texto. Detecta e remove todos os caracteres Unicode de marca d\'água conhecidos.',
      offerDescription: '1.000 chamadas de API incluídas na assinatura Premium',
      features: [
        'Detecta todos os caracteres Unicode de marca d\'água invisíveis conhecidos',
        'Processa até 100.000 caracteres por requisição',
        '1.000 chamadas de API por mês incluídas',
        'API REST simples com respostas JSON',
        'Funciona com texto do ChatGPT, Claude, Gemini',
      ],
    },
  },
  'ms-MY': {
    seo: {
      title: 'API Penyingkir Tanda Air - Buang Tanda Air AI Secara Programatik',
      description: 'API REST untuk membuang tanda air AI yang tidak kelihatan daripada teks. Kesan dan buang semua aksara Unicode tanda air yang diketahui digunakan oleh ChatGPT, Claude, dan Gemini. API JSON mudah dengan masa respons di bawah 100ms.',
    },
    hero: {
      title: 'API Penyingkir Tanda Air',
      subtitle: 'Buang tanda air AI yang tidak kelihatan daripada teks secara programatik. Satu endpoint. Semua jenis tanda air yang diketahui dikesan. Dibina untuk pembangun yang memerlukan teks AI bersih secara berskala.',
      ctaText: 'Dapatkan Akses API',
      secondaryCtaText: 'Lihat Dokumentasi',
    },
    schema: {
      description: 'API REST untuk membuang tanda air AI yang tidak kelihatan daripada teks. Mengesan dan membuang semua aksara Unicode tanda air yang diketahui.',
      offerDescription: '1,000 panggilan API disertakan dengan langganan Premium',
      features: [
        'Mengesan semua aksara Unicode tanda air tidak kelihatan yang diketahui',
        'Memproses sehingga 100,000 aksara setiap permintaan',
        '1,000 panggilan API sebulan disertakan',
        'API REST mudah dengan respons JSON',
        'Berfungsi dengan teks ChatGPT, Claude, Gemini',
      ],
    },
  },
  es: {
    seo: {
      title: 'API Eliminador de Marcas de Agua - Elimina Marcas de Agua de IA Programáticamente',
      description: 'API REST para eliminar marcas de agua invisibles de IA del texto. Detecta y elimina todos los caracteres Unicode de marca de agua conocidos usados por ChatGPT, Claude y Gemini. API JSON simple con tiempos de respuesta inferiores a 100ms.',
    },
    hero: {
      title: 'API Eliminador de Marcas de Agua',
      subtitle: 'Elimina marcas de agua invisibles de IA del texto programáticamente. Un endpoint. Todos los tipos de marca de agua conocidos detectados. Construida para desarrolladores que necesitan texto de IA limpio a escala.',
      ctaText: 'Obtener Acceso a la API',
      secondaryCtaText: 'Ver Documentación',
    },
    schema: {
      description: 'API REST para eliminar marcas de agua invisibles de IA del texto. Detecta y elimina todos los caracteres Unicode de marca de agua conocidos.',
      offerDescription: '1,000 llamadas API incluidas con la suscripción Premium',
      features: [
        'Detecta todos los caracteres Unicode de marca de agua invisibles conocidos',
        'Procesa hasta 100,000 caracteres por petición',
        '1,000 llamadas API por mes incluidas',
        'API REST simple con respuestas JSON',
        'Funciona con texto de ChatGPT, Claude, Gemini',
      ],
    },
  },
  fr: {
    seo: {
      title: 'API de Suppression de Filigrane - Supprimez les Filigranes IA par Programmation',
      description: 'API REST pour supprimer les filigranes IA invisibles du texte. Detecte et supprime tous les caracteres Unicode de filigrane connus utilises par ChatGPT, Claude et Gemini. API JSON simple avec des temps de reponse inferieurs a 100ms.',
    },
    hero: {
      title: 'API de Suppression de Filigrane',
      subtitle: 'Supprimez les filigranes IA invisibles du texte par programmation. Un seul endpoint. Tous les types de filigranes connus detectes. Concue pour les developpeurs qui ont besoin de texte IA propre a grande echelle.',
      ctaText: 'Obtenir l\'Acces API',
      secondaryCtaText: 'Voir la Documentation',
    },
    schema: {
      description: 'API REST pour supprimer les filigranes IA invisibles du texte. Detecte et supprime tous les caracteres Unicode de filigrane connus.',
      offerDescription: '1 000 appels API inclus avec l\'abonnement Premium',
      features: [
        'Detecte tous les caracteres Unicode de filigrane invisibles connus',
        'Traite jusqu\'a 100 000 caracteres par requete',
        '1 000 appels API par mois inclus',
        'API REST simple avec reponses JSON',
        'Fonctionne avec le texte de ChatGPT, Claude, Gemini',
      ],
    },
  },
  ja: {
    seo: {
      title: 'ウォーターマーク除去API - AIウォーターマークをプログラムで削除',
      description: 'テキストから見えないAIウォーターマークを削除するREST API。ChatGPT、Claude、Geminiが使用する全ての既知のUnicodeウォーターマーク文字を検出・削除。100ms未満のレスポンスタイムを持つシンプルなJSON API。',
    },
    hero: {
      title: 'ウォーターマーク除去API',
      subtitle: 'テキストから見えないAIウォーターマークをプログラムで削除。単一のエンドポイント。全ての既知のウォーターマークタイプを検出。大規模でクリーンなAIテキストを必要とする開発者向けに構築。',
      ctaText: 'APIアクセスを取得',
      secondaryCtaText: 'ドキュメントを見る',
    },
    schema: {
      description: 'テキストから見えないAIウォーターマークを削除するREST API。全ての既知のUnicodeウォーターマーク文字を検出・削除。',
      offerDescription: 'プレミアムサブスクリプションに月間1,000 APIコールを含む',
      features: [
        '全ての既知の不可視Unicodeウォーターマーク文字を検出',
        'リクエストごとに最大10万文字を処理',
        '月間1,000 APIコールを含む',
        'JSONレスポンス付きのシンプルなREST API',
        'ChatGPT、Claude、Geminiのテキストに対応',
      ],
    },
  },
  id: {
    seo: {
      title: 'API Penghapus Watermark - Hapus Watermark AI Secara Programatis',
      description: 'API REST untuk menghapus watermark AI yang tidak terlihat dari teks. Mendeteksi dan menghapus semua karakter Unicode watermark yang diketahui yang digunakan oleh ChatGPT, Claude, dan Gemini. API JSON sederhana dengan waktu respons di bawah 100ms.',
    },
    hero: {
      title: 'API Penghapus Watermark',
      subtitle: 'Hapus watermark AI yang tidak terlihat dari teks secara programatis. Satu endpoint. Semua jenis watermark yang diketahui terdeteksi. Dibangun untuk developer yang membutuhkan teks AI bersih dalam skala besar.',
      ctaText: 'Dapatkan Akses API',
      secondaryCtaText: 'Lihat Dokumentasi',
    },
    schema: {
      description: 'API REST untuk menghapus watermark AI yang tidak terlihat dari teks. Mendeteksi dan menghapus semua karakter Unicode watermark yang diketahui.',
      offerDescription: '1.000 panggilan API termasuk dalam langganan Premium',
      features: [
        'Mendeteksi semua karakter Unicode watermark tak terlihat yang diketahui',
        'Memproses hingga 100.000 karakter per permintaan',
        '1.000 panggilan API per bulan termasuk',
        'API REST sederhana dengan respons JSON',
        'Bekerja dengan teks ChatGPT, Claude, Gemini',
      ],
    },
  },
};

const ApiPage: React.FC = () => {
  const { locale } = useLocaleContext();
  const t = translations[locale] || translations.en;

  // Inject SoftwareApplication schema
  useEffect(() => {
    const existingScript = document.querySelector('script[data-schema="software-application"]');
    if (existingScript) {
      existingScript.remove();
    }

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'GPT Watermark Remover API',
      applicationCategory: 'DeveloperApplication',
      operatingSystem: 'Web API',
      description: t.schema.description,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: t.schema.offerDescription,
      },
      featureList: t.schema.features,
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'software-application');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[data-schema="software-application"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [t.schema]);

  return (
    <>
      <SEOHead
        title={t.seo.title}
        description={t.seo.description}
      />

      <div className="container mx-auto px-4 md:px-6 max-w-[1400px]">
          <ApiHero
            title={t.hero.title}
            subtitle={t.hero.subtitle}
            ctaText={t.hero.ctaText}
            secondaryCtaText={t.hero.secondaryCtaText}
          />

          {/* LCP OPTIMIZATION: Lazy load below-fold components in grouped Suspense boundaries
              Grouping reduces React reconciliation overhead and prevents "popcorn" loading effect */}

          {/* API Documentation Section */}
          <Suspense fallback={<div className="min-h-[1000px] animate-pulse bg-neutral-100 dark:bg-neutral-800/30 rounded-xl" />}>
            <ApiFeatures />
            <ApiResponseExample />
            <ApiErrorResponses />
          </Suspense>

          {/* How It Works Section */}
          <Suspense fallback={<div className="min-h-[1200px] animate-pulse bg-neutral-100 dark:bg-neutral-800/30 rounded-xl" />}>
            <ApiHowItWorks />
            <ApiWatermarkTypes />
            <ApiUseCases />
          </Suspense>

          {/* Conversion Section */}
          <Suspense fallback={<div className="min-h-[1400px] animate-pulse bg-neutral-100 dark:bg-neutral-800/30 rounded-xl" />}>
            <ApiTrustSignals />
            <ApiPricing />
            <ApiFaq />
            <ApiCta />
          </Suspense>
        </div>
    </>
  );
};

export default ApiPage;
